import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, ListOrdered, Sparkles } from "lucide-react";
import { grStep1, grStep2, grStep3, grStep3Answer, grStep4 } from "@/lib/api";
import { toBullets } from "@/lib/textBullets";
import TellMeMoreButton from "@/components/TellMeMoreButton";

function Step4Bullets({ items, testId, dotClass }) {
  return (
    <ul
      className="flex flex-col"
      style={{ gap: "8px" }}
      data-testid={testId}
    >
      {items.map((item, idx) => (
        <li
          key={idx}
          className="flex gap-3 items-start"
          data-testid={`${testId}-item-${idx}`}
        >
          <span className={`mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} aria-hidden="true" />
          <span className="text-slate-800" style={{ fontSize: "15px", lineHeight: 1.6 }}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 4-step Guided Review. Never scored, never affects STOP.
 */
export default function GuidedReview({ sessionId, recordId, subject, wrongOptionChosen, onDone }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [microIdx, setMicroIdx] = useState(0);
  const [microSelected, setMicroSelected] = useState(null);
  const [microText, setMicroText] = useState("");
  const [microAttempt, setMicroAttempt] = useState(1);
  const [microMessage, setMicroMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fns = { 1: grStep1, 2: grStep2, 3: grStep3, 4: grStep4 };
    fns[step](sessionId, recordId).then(r => {
      const payload = r.payload;
      // Step 3 fallback: if engine couldn't build micro-steps, glide to Step 4
      if (step === 3 && (payload.skip_to_step4 || !Array.isArray(payload.micro_steps) || payload.micro_steps.length === 0)) {
        setStep(4);
        return;
      }
      setData(payload);
      setLoading(false);
    }).catch(() => {
      // Any error fetching Step 3 → silent forward to Step 4
      if (step === 3) {
        setStep(4);
      } else {
        setLoading(false);
      }
    });
  }, [step, sessionId, recordId]);

  // Defensive guard for Step 3: if data shape is invalid or microIdx escapes range, glide to Step 4.
  useEffect(() => {
    if (step !== 3 || !data) return;
    if (!Array.isArray(data.micro_steps) || data.micro_steps.length === 0 || microIdx >= data.micro_steps.length) {
      setStep(4);
    }
  }, [step, data, microIdx]);

  if (loading || !data) {
    return <div className="text-center text-slate-400 py-12" data-testid="gr-loading">Loading…</div>;
  }

  const advance = () => {
    setMicroSelected(null);
    setMicroText("");
    setMicroMessage(null);
    setMicroAttempt(1);
    if (step < 4) setStep(step + 1);
    else onDone();
  };

  const handleMicroSubmit = async () => {
    const ms = data.micro_steps[microIdx];
    const ans = ms.answer_format === "MCQ" ? microSelected : microText.trim();
    if (!ans) return;
    const r = await grStep3Answer(sessionId, recordId, ms.id, ans, microAttempt);
    setMicroMessage(r.message);
    if (r.next === "next_micro_or_step4") {
      // success
      setTimeout(() => {
        if (microIdx + 1 < data.micro_steps.length) {
          setMicroIdx(microIdx + 1);
          setMicroSelected(null);
          setMicroText("");
          setMicroMessage(null);
          setMicroAttempt(1);
        } else {
          setStep(4);
        }
      }, 700);
    } else if (r.next === "retry") {
      setMicroAttempt(2);
      setMicroSelected(null);
      setMicroText("");
    } else if (r.next === "step4") {
      setTimeout(() => setStep(4), 700);
    }
  };

  return (
    <div className="fade-up max-w-3xl mx-auto" data-testid={`gr-step-${step}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wider">
          Guided Review • Step {step} of 4
        </span>
        <span className="text-xs text-slate-500">Not graded — just learning together</span>
      </div>

      {step === 1 && (
        <Card className="rounded-3xl p-8 mit-shadow border-amber-100 bg-white" data-testid="gr-step1-card">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-amber-600" />
            <h3 className="font-display text-xl font-bold text-slate-800">{data.label}</h3>
          </div>
          <p className="text-slate-600 text-sm mb-3">{data.framing}</p>
          <div className="bg-sky-50 rounded-2xl p-5 text-slate-800 text-lg" data-testid="gr-step1-question">
            {data.question_text}
          </div>
          <div className="text-right mt-6">
            <Button onClick={advance} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white px-6 tap-44" data-testid="gr-step1-next">
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="rounded-3xl p-8 mit-shadow border-amber-100 bg-white" data-testid="gr-step2-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-amber-600" />
            <h3 className="font-display text-xl font-bold text-slate-800">{data.label}</h3>
          </div>
          <p className="text-slate-700 text-base mb-3">{data.framing}</p>
          <div
            className="bg-amber-50 border border-amber-100 rounded-2xl p-5"
            data-testid="gr-step2-brief"
          >
            <Step4Bullets items={toBullets(data.brief)} testId="gr-step2-brief-list" dotClass="bg-amber-500" />
          </div>
          <div className="text-right mt-6">
            <Button onClick={advance} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white px-6 tap-44" data-testid="gr-step2-next">
              Got it <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && (() => {
        if (!Array.isArray(data.micro_steps) || data.micro_steps.length === 0 || microIdx >= data.micro_steps.length) {
          // useEffect above will route to Step 4; render a soft loading shim meanwhile.
          return <div className="text-center text-slate-400 py-12" data-testid="gr-step3-skip">…</div>;
        }
        const ms = data.micro_steps[microIdx];
        const msOptions = Array.isArray(ms.options) ? ms.options : [];
        const renderAsMcq = ms.answer_format === "MCQ" && msOptions.length >= 2;
        return (
          <Card className="rounded-3xl p-8 mit-shadow border-amber-100 bg-white" data-testid="gr-step3-card">
            <div className="flex items-center gap-2 mb-3">
              <ListOrdered className="w-5 h-5 text-amber-600" />
              <h3 className="font-display text-xl font-bold text-slate-800">{data.label}</h3>
              <span className="ml-auto text-xs font-semibold text-slate-500">
                Step {microIdx + 1} / {data.micro_steps.length} • Try {microAttempt}/2
              </span>
            </div>
            <p className="text-slate-800 text-lg mb-2" data-testid="gr-microstep-prompt">{ms.prompt}</p>
            {ms.hint && <p className="text-slate-500 text-sm mb-4">{ms.hint}</p>}

            {renderAsMcq ? (
              <div className="space-y-2" role="radiogroup">
                {msOptions.map((opt, i) => {
                  const active = microSelected === opt;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setMicroSelected(opt)}
                      className={`w-full text-left px-4 py-3 rounded-2xl border-2 transition-colors tap-44 ${active ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-amber-300 hover:bg-amber-50"}`}
                      data-testid={`gr-micro-option-${i}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            ) : (
              <input
                value={microText}
                onChange={e => setMicroText(e.target.value)}
                className="w-full px-5 py-4 rounded-2xl border-2 border-slate-200 focus:border-amber-400 focus:outline-none"
                data-testid="gr-micro-tita"
                placeholder="Your answer…"
              />
            )}

            {microMessage && (
              <div className="mt-4 px-4 py-3 rounded-2xl bg-sky-50 text-sky-800 text-sm" data-testid="gr-micro-feedback">
                {microMessage}
              </div>
            )}

            <div className="text-right mt-6">
              <Button onClick={handleMicroSubmit} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white px-6 tap-44" data-testid="gr-micro-submit">
                Check
              </Button>
            </div>
          </Card>
        );
      })()}

      {step === 4 && (
        <Card className="rounded-3xl p-8 mit-shadow border-emerald-100 bg-emerald-50" data-testid="gr-step4-card">
          <h3 className="font-display text-xl font-bold text-emerald-800 mb-3">{data.label}</h3>
          <div className="bg-white rounded-2xl p-5 mb-3" data-testid="gr-step4-crisp">
            <Step4Bullets items={toBullets(data.crisp)} testId="gr-step4-crisp-list" dotClass="bg-emerald-500" />
          </div>
          {data.elaborate && (
            <div className="bg-white border-dashed border border-slate-200 rounded-2xl p-5 mb-3" data-testid="gr-step4-elaborate">
              <div className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-3">To elaborate</div>
              <Step4Bullets items={toBullets(data.elaborate)} testId="gr-step4-elaborate-list" dotClass="bg-slate-400" />
            </div>
          )}
          <p className="text-emerald-800 font-semibold mb-5" data-testid="gr-step4-encouragement">{data.encouragement}</p>
          <TellMeMoreButton scope="guided_review_step4" recordId={recordId} subject={subject} wrongOptionChosen={wrongOptionChosen} label="Ask Mitraa to simplify" />
          <div className="text-right mt-5">
            <Button onClick={onDone} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 tap-44" data-testid="gr-finish">
              Continue
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
